/** The organisation page's "Billing" card: its subscription (Stripe or mock), staff's override (comped: the plan
 * staff assign stays whatever Stripe says), and coupons: grant one (its current subscription's next invoice, or its
 * next checkout or Lux purchase) and the company's coupon history. Audited. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, unwrap } from "../../api/client";
import { Badge, Button, Card, Field, Spinner, Switch, useToast } from "../../components/ui";
import { paidSeats, type SeatSubscription } from "../../lib/billing";
import { useBilling } from "../BillingPanel";
import { FormattedMessage } from "react-intl";
import { useT, type Translate } from "../../i18n/I18nProvider";
import { useFmt, type Fmt } from "./format";

const STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "info"> = { active: "ok", trialing: "ok", past_due: "warn", canceled: "danger", unpaid: "danger", incomplete: "info" };

type SubscriptionState = { status: string; cancel_at_period_end: boolean; current_period_end?: string | null };

/** The subscription's state in words (the admin's reading of lib/billing's subscriptionLine). */
function subscriptionState(sub: SubscriptionState, t: Translate, date: Fmt["date"]): string {
  const end = sub.current_period_end ? date(sub.current_period_end) : null;
  if (sub.status === "past_due") return t("admin.orgBilling.statePastDue");
  if (sub.status === "canceled") return t("admin.orgBilling.stateCanceled");
  if (sub.status === "incomplete") return t("admin.orgBilling.stateIncomplete");
  if (sub.cancel_at_period_end) return end ? t("admin.orgBilling.stateEndsOn", { date: end }) : t("admin.orgBilling.stateEndsAtPeriodEnd");
  return end ? t("admin.orgBilling.stateRenewsOn", { date: end }) : t("admin.orgBilling.stateActive");
}

/** A reduction of extra users waiting for the renewal, in words (null when none; lib/billing's pendingSeatsText). */
function pendingSeats(seats: SeatSubscription, t: Translate, date: Fmt["date"]): string | null {
  const paid = paidSeats(seats);
  if (!paid || seats.scheduled_quantity == null || seats.scheduled_quantity >= paid) return null;
  const values = { next: seats.scheduled_quantity, paid };
  return seats.scheduled_at ? t("admin.orgBilling.pendingSeatsOn", { ...values, date: date(seats.scheduled_at) }) : t("admin.orgBilling.pendingSeatsRenewal", values);
}

export function OrgBilling({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const { lux, cents, date } = useFmt();
  const billing = useBilling(orgId, true);
  const history = useQuery({
    queryKey: ["org-coupons", orgId],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs/{org_id}/coupons", { params: { path: { org_id: orgId } } })),
  });
  const coupons = useQuery({ queryKey: ["admin-coupons"], queryFn: () => unwrap(api.GET("/v1/admin/coupons")) });
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const comp = useMutation({
    mutationFn: (comped: boolean) =>
      unwrap(api.PATCH("/v1/admin/orgs/{org_id}/billing", { params: { path: { org_id: orgId } }, body: { comped, note: note.trim() || null } })),
    onSuccess: (data) => {
      qc.setQueryData(["billing", orgId, true], data);
      qc.invalidateQueries({ queryKey: ["org-plan", orgId] });
      toast(data.comped ? t("admin.orgBilling.compedToast") : t("admin.orgBilling.uncompedToast"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const grant = useMutation({
    mutationFn: () =>
      unwrap(api.POST("/v1/admin/orgs/{org_id}/coupons", { params: { path: { org_id: orgId } }, body: { code, note: grantNote.trim() || null } })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["org-coupons", orgId] });
      qc.invalidateQueries({ queryKey: ["billing", orgId] });
      toast(r.status === "redeemed" ? t("admin.orgBilling.couponApplied", { code: r.code }) : t("admin.orgBilling.couponGranted", { code: r.code, appliesTo: r.applies_to === "lux" ? "lux" : "checkout" }));
      setCode("");
      setGrantNote("");
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!billing.data) return <Card title={t("admin.orgBilling.title")}>{billing.isLoading ? <Spinner /> : errorMessage(billing.error)}</Card>;
  const b = billing.data;
  const sub = b.subscription;
  const active = (coupons.data?.items ?? []).filter((c) => c.active);
  return (
    <Card title={t("admin.orgBilling.title")} description={t("admin.orgBilling.description")}>
      <div className="stack">
        <div className="billing-row">
          <div>
            {sub ? (
              <>
                <b>{t("admin.orgBilling.planBilling", { plan: sub.plan, billing: sub.billing })}</b>{" "}
                <Badge tone={STATUS_TONE[sub.status] ?? "info"}>{t("admin.orgBilling.subscriptionStatus", { status: sub.status })}</Badge>
                {sub.coupon_code && <Badge tone="info" plain><span className="i18n-ltr">{sub.coupon_code}</span></Badge>}
                <div className="muted small">
                  {subscriptionState(sub, t, date)} · <span dir="auto">{sub.provider}</span> <span className="mono i18n-ltr">{sub.provider_ref}</span>
                </div>
                {b.seats_subscription && (
                  <div className="muted small">
                    {t("admin.orgBilling.seatsSubscription", { quantity: b.seats_subscription.quantity, status: b.seats_subscription.status })}
                    {pendingSeats(b.seats_subscription, t, date) ? <> · {pendingSeats(b.seats_subscription, t, date)}</> : null}
                  </div>
                )}
                {b.grace_ends_at && <div className="small" style={{ color: "var(--warn)" }}>{t("admin.orgBilling.graceEnds", { date: date(b.grace_ends_at) })}</div>}
              </>
            ) : (
              <span className="muted">{b.available ? t("admin.orgBilling.noSubscription") : t("admin.orgBilling.noSubscriptionOff")}</span>
            )}
            {(b.lux_seats ?? []).length > 0 && (
              <div className="small">
                <b>{t("admin.orgBilling.luxSeats")}</b>
                {(b.lux_seats ?? []).map((x) => (
                  <div key={x.id} className="muted">
                    {t("admin.orgBilling.luxSeat", {
                      quantity: x.quantity,
                      term: x.term,
                      lux: lux(x.lux),
                      price: cents(x.price_minor, x.currency),
                      starts: date(x.starts_at),
                      ends: date(x.ends_at),
                      status: x.status,
                      autoRenew: x.auto_renew ? "yes" : "no",
                    })}
                  </div>
                ))}
              </div>
            )}
            {b.stripe_customer_id && (
              <div className="small">
                <FormattedMessage
                  id="admin.orgBilling.stripeCustomer"
                  values={{
                    id: (
                      <a href={`https://dashboard.stripe.com/customers/${b.stripe_customer_id}`} target="_blank" rel="noreferrer" className="mono i18n-ltr">
                        {b.stripe_customer_id}
                      </a>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="billing-row">
          <div style={{ minWidth: 0, flex: 1 }}>
            <b>
              <ShieldCheck size={14} style={{ verticalAlign: -2 }} /> {t("admin.orgBilling.comp")}
            </b>
            <div className="muted small">{t("admin.orgBilling.compHelp")}</div>
            <input className="input" dir="auto" style={{ marginTop: 6 }} placeholder={t("admin.orgBilling.noteAudited")} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <Switch checked={b.comped} onChange={(v) => comp.mutate(v)} label={b.comped ? t("admin.orgBilling.comped") : t("admin.orgBilling.stripeDecides")} />
        </div>
        <div>
          <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
            <Gift size={14} style={{ verticalAlign: -2 }} /> {t("admin.orgBilling.grantTitle")}
          </div>
          <div className="form-grid">
            <Field label={t("admin.orgBilling.coupon")}>
              <select className="select" value={code} onChange={(e) => setCode(e.target.value)} aria-label={t("admin.orgBilling.coupon")}>
                <option value="">{t("admin.orgBilling.chooseCoupon")}</option>
                {active.map((c) => (
                  <option key={c.id} value={c.code}>
                    {t("admin.orgBilling.couponOption", { code: c.code, name: c.name, appliesTo: c.applies_to === "lux" ? "lux" : "subscription" })}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("admin.orgBilling.noteAudited")}>
              <input className="input" dir="auto" maxLength={500} value={grantNote} onChange={(e) => setGrantNote(e.target.value)} placeholder={t("admin.orgBilling.grantNotePlaceholder")} />
            </Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <Button icon={<Gift />} disabled={!code} loading={grant.isPending} onClick={() => grant.mutate()}>
              {t("admin.orgBilling.grantCoupon")}
            </Button>
          </div>
        </div>
        {(history.data?.items ?? []).length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.orgBilling.coupon")}</th>
                  <th>{t("admin.orgBilling.colHow")}</th>
                  <th>{t("admin.orgBilling.colStatus")}</th>
                  <th>{t("admin.orgBilling.colWhen")}</th>
                </tr>
              </thead>
              <tbody>
                {history.data!.items.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <b className="i18n-ltr">{r.code}</b> <span className="muted small" dir="auto">{r.coupon_name}</span>
                      {r.note && <div className="muted small" dir="auto">{r.note}</div>}
                    </td>
                    <td className="small">{r.source === "grant" ? t("admin.orgBilling.sourceGrant") : t("admin.orgBilling.sourceCheckout")}</td>
                    <td>
                      <Badge tone={r.status === "redeemed" ? "ok" : r.status === "canceled" || r.status === "over_limit" ? "danger" : "info"}>{t("admin.orgBilling.redemptionStatus", { status: r.status })}</Badge>
                    </td>
                    <td className="small">{date(r.redeemed_at ?? r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
